import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { HttpService } from '../../../utility/http-service';

export interface FoldingEditorData {
  dataSetId: string;
  dataSetName: string;
}

@Component({
  selector: 'app-folding-editor',
  standalone: true,
  templateUrl: './folding-editor.component.html',
  styleUrls: ['./folding-editor.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatCheckboxModule,
    MatFormFieldModule,
  ],
})
export class FoldingEditorComponent implements OnInit {
  form!: FormGroup;
  loading = true;
  saving = false;
  error: string | null = null;

  constructor(
    private fb: FormBuilder,
    private http: HttpService,
    public dialogRef: MatDialogRef<FoldingEditorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FoldingEditorData,
  ) {}

  async ngOnInit(): Promise<void> {
    this.form = this.fb.group({
      table: ['', Validators.required],
      cascadeField: [''],
      keys: this.fb.array([]),
    });

    try {
      const raw = await this.http.getWait<string | null>(
        `ClientSettings/GetFoldingPayload?dataSetId=${this.data.dataSetId}`
      );
      if (raw) {
        const config = JSON.parse(raw);
        this.form.patchValue({ table: config.Table ?? '', cascadeField: config.CascadeField ?? '' });
        (config.Keys ?? []).forEach((k: any) => this.addKey(k));
      }
    } catch {
      this.error = 'Failed to load folding configuration.';
    } finally {
      this.loading = false;
    }
  }

  get keys(): FormArray {
    return this.form.get('keys') as FormArray;
  }

  addKey(existing?: any): void {
    const norm = existing?.Normalization ?? {};
    this.keys.push(this.fb.group({
      field: [existing?.Field ?? '', Validators.required],
      stripChars: [(norm.StripChars ?? []).join(', ')],
      truncateTo: [norm.TruncateTo ?? null],
      caseInsensitive: [norm.CaseInsensitive ?? true],
    }));
  }

  removeKey(i: number): void {
    this.keys.removeAt(i);
  }

  private buildPayload(): string {
    const val = this.form.value;
    const config = {
      Table: val.table,
      CascadeField: val.cascadeField || null,
      Keys: val.keys.map((k: any) => ({
        Field: k.field,
        Normalization: {
          StripChars: k.stripChars
            ? k.stripChars.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0)
            : [],
          TruncateTo: k.truncateTo ? Number(k.truncateTo) : null,
          CaseInsensitive: k.caseInsensitive,
        },
      })),
    };
    return JSON.stringify(config);
  }

  save(): void {
    if (this.form.invalid) return;
    this.saving = true;
    this.error = null;

    const body = { dataSetId: this.data.dataSetId, payload: this.buildPayload() };
    this.http.postReturn('ClientSettings/SaveFoldingPayload', () => {
      this.dialogRef.close(true);
    }, undefined, body, () => {
      this.error = 'Save failed.';
      this.saving = false;
    });
  }

  clear(): void {
    const body = { dataSetId: this.data.dataSetId, payload: null };
    this.http.postReturn('ClientSettings/SaveFoldingPayload', () => {
      this.dialogRef.close(true);
    }, undefined, body, () => {
      this.error = 'Clear failed.';
    });
  }
}
